/**
 * The seam between the session store and the UI.
 *
 * Every component in this chunk is a pure function of `SessionState` plus a handful of
 * callbacks. Nothing below this file imports the store, subscribes to anything, or owns
 * mutable session state — which is what makes all fifteen UX states renderable from a
 * fixture, and what makes `/dev/states` a real substitute for a piano.
 *
 * The store (Chunk D) supplies a `SessionView` and wraps the tree in `SessionProvider`.
 * Everything on it except `state` and `dispatch` is optional: the UI degrades to
 * sensible local behaviour when a capability has not been wired yet, so this chunk and
 * the store chunk can land in either order.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import {
  createInitialSessionState,
  type BranchSuggestion,
  type CanonicalChord,
  type ChordRecognizer,
  type ModelChoice,
  type PreferenceHints,
  type SessionAction,
  type SessionState,
} from '@hc/shared';
import type { CapabilityReport } from '../../capabilities.js';

/**
 * A record of the most recent save, for the confirmation toast.
 *
 * It is a value on the view rather than a piece of `SessionState` because it is UI
 * ephemera with a four-second life — putting it in the reducer would mean persisting it,
 * replaying it, and restoring a stale toast after a refresh.
 */
export interface SaveReceipt {
  sketchId: string;
  title: string;
  savedAt: number;
}

/**
 * Fetches the long-form explanation for a suggestion.
 *
 * Deliberately not called until the sheet opens: the prose that fits on a card is
 * generated with the branch set, and the paragraph behind it is not worth a token or a
 * millisecond until someone actually asks for it.
 */
export type ExplanationFetcher = (
  suggestion: BranchSuggestion,
  signal: AbortSignal,
) => Promise<string>;

/**
 * Where the player is, not what the app is doing.
 *
 * `'piano'` is the product's home: a microphone is open and chords arrive by being played.
 * `'on-the-go'` is the same product without an instrument — the first chord comes from a
 * picker, the next from a tapped branch card or a typed symbol, and no `AudioContext` is
 * ever created. Both drive the identical session reducer through the identical branch
 * pipeline; the mode decides how a chord is *selected*, never what happens to it.
 *
 * It lives on the view rather than in `SessionState` because it describes the device and
 * the moment, not the music. See `state/modePreference.ts`.
 */
export type SessionMode = 'piano' | 'on-the-go';

/**
 * The settings the session layer owns. The rest of the settings sheet is the reducer's.
 *
 * Declared here rather than beside the host because the host is above this file: every
 * component that reads `view.settings` codes against this shape, and a fixture supplying
 * one has to be able to name it.
 */
export interface SessionSettings {
  speakReplies: boolean;
  /**
   * Whether suggestions and their prose come from a model over the network, or from the
   * built-in engine.
   *
   * **Off by default**, which is the product's posture rather than a cautious default:
   * `@hc/shared`'s `heuristics/` answers instantly, offline, and with the same wire shape,
   * so the ordinary session never makes a request. On, every branch set, explanation and
   * unmatched command goes to the server exactly as it always did.
   *
   * It is the gate on more than the requests. The model tier, the prompt sheet, the
   * look-ahead, the lenses and the intention field all exist only to shape a prompt, so
   * they are hidden while this is off — a control that cannot reach anything is a lie
   * about what the app is doing.
   */
  aiCompanion: boolean;
  prefetch: boolean;
  /**
   * Harvest a labelled recognition dataset while playing: every audio guess gets a ✓/✗
   * offer, and the answer — including "nobody answered" — is written to the verdict log.
   * Off by default, and off means today's screen exactly.
   */
  collectRecognitions: boolean;
  /**
   * Play each chord as it lands, On the Go.
   *
   * **On by default**, unlike everything else here, because it is the point of the mode:
   * without an instrument a graph of chords is a picture of music nobody has heard.
   * Ignored at the piano and not merely as an optimisation — the microphone would hear
   * the speaker and feed the recogniser its own output.
   */
  soundOnTheGo: boolean;
  /**
   * Which model tier serves this player's requests. A tier name, never a provider ID;
   * the server owns the mapping. `'luna'` is the default — the fastest tier, because a
   * default is what a player meets before they know there is a switch.
   */
  modelChoice: ModelChoice;
  /**
   * Show what is actually sent to the model: the composed prompts, the tier that answered,
   * whether it came from the cache, and how long it took.
   *
   * Off by default and off costs nothing — no `debug` flag on any request, nothing kept,
   * and the sheet is not mounted. It is diagnostics, which is also why the log it fills
   * is a plain module rather than session state.
   */
  showPrompts: boolean;
}

export interface SessionView {
  state: SessionState;
  dispatch: (action: SessionAction) => void;

  /** What this browser can do. Drives the unsupported screen. */
  capabilities?: CapabilityReport;

  /**
   * Ask for the microphone and unlock the `AudioContext`.
   *
   * Called from the "Start playing" tap and nowhere else, because both of those things
   * require a user gesture and this is the only gesture guaranteed to be one.
   */
  startListening?: (options: { intention?: string }) => void | Promise<void>;

  /**
   * Re-request the branch set for a node. The retry glyph in the branch column.
   *
   * `hint` is the steer from "something else — somewhere distant", and applies to this
   * one request only; the retry glyph passes no options at all.
   */
  retryBranches?: (nodeId: string, opts?: { hint?: string }) => void;

  /** Long-form explanation for the explanation sheet. */
  fetchExplanation?: ExplanationFetcher;

  /**
   * A restorable snapshot found in IndexedDB on load. Presence of this is what shows the
   * resume card; the store clears it once the player has chosen.
   */
  resumeSnapshot?: SessionState | null;
  resumeSession?: () => void;
  startFresh?: () => void;

  /**
   * Leave the session and return to the entry screen.
   *
   * Not the same act as `newSession`, and the difference is visible: this puts the whole
   * state back to the shape a cold start has — empty path, `listening: 'idle'`,
   * `micStatus: 'unrequested'` — which is what `screenFor` reads as "home", and hands the
   * microphone back on the way out. `newSession` keeps you in the session screen with the
   * stream still open, ready for the first chord of the next idea.
   *
   * Both discard the working path, so the header asks first. See `SessionHeader`.
   */
  endSession?: () => void;
  /** Discard the path and start again in the mode already chosen. */
  newSession?: () => void;

  /** The most recent save, or null. Presence shows the toast. */
  lastSave?: SaveReceipt | null;
  dismissSave?: () => void;
  renameSketch?: (sketchId: string, title: string) => void;
  viewSketch?: (sketchId: string) => void;
  /**
   * Open the sketchbook itself. Absent where there is no router — under a fixture, in a
   * component test — and the home screen simply omits the link there.
   */
  viewSketchbook?: () => void;

  /**
   * A chord typed rather than played — manual-entry mode.
   *
   * When the store does not supply one, `useManualChordEntry` falls back to synthesising
   * a `DetectedChord` and dispatching `CHORD_DETECTED`, so manual entry works before the
   * store grows a first-class path for it. The host wires this to `selectChord`; they are
   * the same funnel reached from two affordances.
   */
  onManualChord?: (chord: CanonicalChord) => void;

  // -------------------------------------------------------------------------
  // On the Go
  //
  // Every one of these is optional and every one of them is implemented by
  // `state/sessionHost.tsx`. Absent means the capability has not been wired — the UI
  // degrades to what it did before, which is the whole reason the fixtures still work.
  // -------------------------------------------------------------------------

  /** Absent means `'piano'`. */
  mode?: SessionMode;
  /**
   * Switch modes. The tap that calls this is a user gesture, which is what lets the host
   * resume a suspended `AudioContext` on the way back to the piano.
   */
  setMode?: (mode: SessionMode) => void;

  /**
   * Begin a session with no microphone and no `AudioContext` — the train, the bus, the
   * sofa. The counterpart to `startListening`, and deliberately not a variant of it: it
   * must be impossible to reach `getUserMedia` from this path.
   */
  startOnTheGo?: (options?: { intention?: string }) => void;

  /**
   * The selection funnel: a branch card tapped, a chord picked, a symbol typed. Goes
   * through the same `CHORD_DETECTED` a played chord does, stamped `source: 'manual'`.
   */
  selectChord?: (chord: CanonicalChord) => void;

  /**
   * Hear a chord without choosing it — "Hear it", beside "Take this" and in the history
   * detail. "I just want to be able to play the chords I see!"
   *
   * The counterpart to `selectChord` and deliberately not a variant of it: **nothing is
   * dispatched**. No node, no branch request, no verdict, nothing to undo. A player
   * deciding between five cards strikes each of them two or three times, and every one of
   * those must cost exactly a sound.
   *
   * It is the same player and the same voicing the landing sound uses, so a chord is
   * identical previewed and taken — the point of the affordance is that what you heard is
   * what you are about to get.
   *
   * **Absent at the piano**, where the microphone is open and would hear the speaker, and
   * absent with sound off, where a button promising a sound would be a promise the
   * settings have already refused. Absence is the whole gate: a component with no
   * capability draws no button.
   */
  previewChord?: (chord: CanonicalChord) => void;

  /**
   * Go back to a node in the history, discarding everything after it. Tapping a history
   * node is the whole gesture; the host works out the step count. There is no redo — the
   * forward path is gone, and offering to un-discard it would be a second history to keep
   * consistent for a move nobody makes twice.
   */
  stepBackTo?: (nodeId: string) => void;

  /**
   * Keep this. `'progression'` (the default) saves the whole path; `'last-transition'`
   * saves just the move that was made, which is the thing worth keeping surprisingly
   * often. Resolves to the receipt that drives the toast, or `null` when there is nothing
   * to save.
   */
  saveSketch?: (scope?: 'progression' | 'last-transition') => Promise<SaveReceipt | null>;

  /**
   * Record the move just made as one this player liked — "I like that transition".
   *
   * It goes through the view for the same reason `resetPreferences` does: only the host
   * holds the `PreferencesController`, and the preference hints are not session state.
   * Returns **false** when there was no transition to record (a path of fewer than two
   * chords), so the command bar can say something honest instead of "Noted." about
   * nothing. Absent means no controller is running, and the bar degrades to saying so.
   */
  markLiked?: () => boolean;

  /**
   * The live recogniser's pause/resume, or `null` when no microphone pipeline exists yet
   * — which is the normal state On the Go. Voice input borrows the microphone through
   * this; so does the mode toggle.
   */
  recognizerControls?: { pause(): void; resume(): void } | null;

  /**
   * The live recogniser itself, for chrome that needs more of it than pause and resume.
   *
   * Today that is exactly one thing: the first-run calibration sheet, which subscribes to
   * `diagnostics` to measure the room and writes `energyGateDb` back. Deliberately a
   * `Pick` rather than the whole interface — nothing above the composition root may
   * `start` or `stop` the microphone, and the type says so.
   *
   * Absent until a recogniser exists, which is always the case On the Go and until the
   * first `startListening` at the piano.
   */
  recognizer?: Pick<ChordRecognizer, 'on' | 'setParams' | 'getParams'>;

  settings?: SessionSettings;
  updateSettings?: (patch: Partial<SessionSettings>) => void;

  /**
   * Forget everything the app has learned about this player.
   *
   * It goes through the view rather than straight to `sketchbook/queries.ts` because the
   * host's `PreferencesController` holds the tallies in memory and writes them back on the
   * next chord — clearing only the stored copy would look like a reset and then quietly
   * undo itself. Absent means no controller is running, and the settings sheet falls back
   * to clearing storage directly, which is correct in that case.
   */
  resetPreferences?: () => Promise<PreferenceHints>;
}

const SessionViewContext = createContext<SessionView | null>(null);

export function SessionProvider({
  value,
  children,
}: {
  value: SessionView;
  children: ReactNode;
}): React.JSX.Element {
  return <SessionViewContext.Provider value={value}>{children}</SessionViewContext.Provider>;
}

/**
 * An inert view: real, empty session state and callbacks that do nothing.
 *
 * This is what the app renders before the store is mounted, and it is genuinely useful
 * rather than a placeholder — it produces the home screen, which is the correct first
 * screen anyway.
 */
export function createIdleSessionView(overrides: Partial<SessionView> = {}): SessionView {
  return {
    // No lenses, for the same reason the live store starts with none: the idle view is
    // what the home screen renders, and a settings sheet opened over it should show what
    // a fresh session would actually send. See `LIVE_DEFAULT_PERSPECTIVES`.
    state: createInitialSessionState({ sessionId: 'idle', startedAt: 0, perspectives: [] }),
    dispatch: () => {},
    // `capabilities` is deliberately absent rather than optimistic. `App` probes the
    // browser and fills it in, and a default of "everything works" here would shadow the
    // probe — which is exactly the bug that would ship the unsupported screen as dead
    // code. Absent means "not yet known", and the UI treats that as "carry on".
    ...overrides,
  };
}

/**
 * Read the view from context, or fall back to the idle one.
 *
 * Falling back rather than throwing is deliberate: a missing provider should show the
 * home screen, not a red error overlay in front of someone holding a chord.
 */
export function useSessionView(override?: SessionView): SessionView {
  const fromContext = useContext(SessionViewContext);
  const fallback = useMemo(() => createIdleSessionView(), []);
  return override ?? fromContext ?? fallback;
}
